import React, { Component, Fragment } from 'react';
import Modal from '../../../Modal/Modal';
import PropTypes from 'prop-types';
import {
    EmvioSelect,
    EmvioValidatedInput,
    EmvioValidatedForm,
    EmvioValidatedButton,
    EmvioButton,
    EmvioNewSelect
} from '@nexio/emvio-form-components';
import {
    EmvioConfirmation
} from '@nexio/emvio-util-ui';
import _ from 'lodash';
import { doesPropExistAndHasItChanged } from '@nexio/emvio-util-app';
import { merchantShape } from '../../../../utils/propTypes';
import classNames from 'classnames';
import './UserRouteForm.scss';

export const ruleTypes = {
    TARGET_ALLOCATION: 'TARGET_ALLOCATION',
    ITEM_RULE: 'ITEM_RULE'
};

export const inputTypes = {
    REGEX: 'regex',
    CONTAINS: 'stringContains',
    DOES_NOT_CONTAIN: 'stringDoesNotContain',
    MATCHES_EXACTLY: 'stringIsExactly',
    DOES_NOT_MATCH_EXACTLY: 'stringIsNotExactly'
};

export function getMerchantSupportedCurrencies(merchant) {
    const paymentOptions = _.get(merchant, 'paymentOptions') || [];
    const currencies = paymentOptions.reduce((acc, item) => {
        acc.push(item.currency);

        return acc;
    }, []);

    return _.uniq(currencies);
}

const allAttributes = ['item', 'description', 'type'];
const maxAttributeCount = _.size(allAttributes);

export class UserRouteForm extends Component {
    static propTypes = {
        modalTitle: PropTypes.string,
        uniqueModalName: PropTypes.string,
        onSubmit: PropTypes.func.isRequired,
        clearRule: PropTypes.func.isRequired,
        rule: PropTypes.any,
        rules: PropTypes.array,
        merchants: PropTypes.arrayOf(merchantShape),
        paymentMerchants: PropTypes.array,
        isEditRulesLoading: PropTypes.bool,
        isEditRulesSuccessful: PropTypes.bool
    };

    static defaultProps = {
        rule: {},
        rules: [],
        merchants: []
    };

    constructor(props) {
        super(props);

        this.state = {
            ruleType: ruleTypes.TARGET_ALLOCATION,
            currency: '',
            targetAllocations: [],
            merchantId: '',
            itemRule: {}
        };

        this.submitButtonRef = React.createRef();
        this.deleteButtonRef = React.createRef();
        this.popoverDeleteRuleRef = React.createRef();
    }

    componentDidUpdate(prevProps) {
        if (doesPropExistAndHasItChanged(prevProps, this.props, 'rule')) {
            const { rule } = this.props;

            if (rule.ruleType === ruleTypes.TARGET_ALLOCATION) {
                this.setState({
                    currency: rule.currency,
                    targetAllocations: this.buildTargetAllocations(rule.currency, rule.weights),
                    ruleType: rule.ruleType
                });
            } else if (rule.ruleType === ruleTypes.ITEM_RULE) {

                const builtItemRule = _.map(rule.itemRule, (value, key) => {
                    const inputType = this.determineInputType(value);
                    let trimmedInput = this.extractValueFromRegex(value, inputType);
                    if (inputType !== 'regex') {
                        trimmedInput = this.unescapeRegex(trimmedInput);
                    }

                    return { attribute: key, value: trimmedInput, inputType, key: crypto.randomUUID() };
                });

                this.setState({
                    ruleType: rule.ruleType,
                    merchantId: rule.merchantId,
                    itemRule: rule.itemRule,
                    builtItemRule
                });
            }
        }
    }

    doesContainRegex = (input) => {
        let hasUnescapedSlash = false;
        const regexSymbols = ['\\', '^', '$', '.', '*', '+', '?', '(', ')', '[', ']', '{', '}', '|'];

        return _.some(input, (character, index) => {
            if (_.includes(regexSymbols, character)) {
                if (character === '\\') {
                    hasUnescapedSlash = !hasUnescapedSlash;
                } else if (index === 0) {
                    return true;
                } else if (!hasUnescapedSlash) {
                    return true;
                } else {
                    hasUnescapedSlash = !hasUnescapedSlash;
                }
            } else if (hasUnescapedSlash) {
                return true;
            }
        });
    };

    unescapeRegex = (input) => {
        let inputArray = [...input];

        _.each(inputArray, (character, index) => {
            if (character === '\\') {
                if (index + 1 !== input.length && input[index + 1] === '\\') {
                    inputArray[index + 1] = '';
                } else {
                    inputArray[index] = '';
                }
            }
        });

        return _.join(inputArray, '');
    };

    doesEndWith = (input, endToMatch) => {
        let doesEndWith = false;
        let reverseInput = _.reverse([...input]);
        const reverseEnd = _.reverse([...endToMatch]);

        for (let i = 0; i < reverseInput.length; i++) {
            if (i >= reverseEnd.length) {
                if (reverseInput[i] === '\\') {
                    doesEndWith = !doesEndWith;
                } else {
                    return doesEndWith;
                }
            } else if (reverseInput[i] !== reverseEnd[i]) {
                return false;
            } else {
                doesEndWith = true;
            }
        }

        return doesEndWith;
    };

    determineInputType = (input) => {
        if (_.startsWith(input, '^((?!') && this.doesEndWith(input, ').)*$') && !this.doesContainRegex(this.extractValueFromRegex(input, inputTypes.DOES_NOT_CONTAIN))) {
            return inputTypes.DOES_NOT_CONTAIN;
        } else if (_.startsWith(input, '^') && this.doesEndWith(input, '$') && !this.doesContainRegex(this.extractValueFromRegex(input, inputTypes.MATCHES_EXACTLY))) {
            return inputTypes.MATCHES_EXACTLY;
        } else if (_.startsWith(input, '^(?!') && this.doesEndWith(input, '$)') && !this.doesContainRegex(this.extractValueFromRegex(input, inputTypes.DOES_NOT_MATCH_EXACTLY))) {
            return inputTypes.DOES_NOT_MATCH_EXACTLY;
        } else if (!this.doesContainRegex(input)) {
            return inputTypes.CONTAINS;
        } else {
            return inputTypes.REGEX;
        }
    };

    extractValueFromRegex = (input, inputType) => {
        if (inputType === inputTypes.DOES_NOT_CONTAIN) {
            input = input.slice(5, -5);
        } else if (inputType === inputTypes.MATCHES_EXACTLY) {
            input = input.slice(1, -1);
        } else if (inputType === inputTypes.DOES_NOT_MATCH_EXACTLY) {
            input = input.slice(4, -2);
        }

        return input;
    };

    convertStringToRegex = (input, type) => {
        switch (type) {
            case inputTypes.CONTAINS:
                return _.escapeRegExp(input);
            case inputTypes.DOES_NOT_CONTAIN:
                return `^((?!${_.escapeRegExp(input)}).)*$`;
            case inputTypes.MATCHES_EXACTLY:
                return `^${_.escapeRegExp(input)}$`;
            case inputTypes.DOES_NOT_MATCH_EXACTLY:
                return `^(?!${_.escapeRegExp(input)}$)`;
            default:
                return input;
        }
    };

    handleOnClose = () => {
        this.clearState();
        this.props.clearRule();
    };

    clearState = () => {
        this.setState({
            currency: '',
            targetAllocations: [],
            ruleType: ruleTypes.TARGET_ALLOCATION,
            merchantId: '',
            itemRule: {},
            builtItemRule: []
        });
    };

    hasExternalErrors = () => {
        const isTargetAllocationRule = this.state.ruleType === ruleTypes.TARGET_ALLOCATION;
        if (isTargetAllocationRule) {
            return this.getTotalAllocationPercentage() !== 100;
        } else {

            return _.some(this.state.builtItemRule, (attribute) => {

                return !attribute.attribute || (!attribute.value && !_.isFinite(attribute.value)) || !attribute.inputType;
            });
        }
    };

    buildTargetAllocations = (currency, weights) => {
        const allocations = _(this.props.paymentMerchants)
            .filter((paymentMerchant) => _.includes(getMerchantSupportedCurrencies(paymentMerchant), currency))
            .map((paymentMerchant) => {
                const { merchantId } = paymentMerchant;

                let weight = '';
                if (weights) {
                    weight = _.get(_.find(weights, { merchantId }), 'weight') || 0;
                    weight = _.toString(_.toNumber(weight));
                }

                return {
                    merchantId,
                    weight
                };
            }).value();

        const summedWeight = _.sumBy(allocations, (allocation) => {
            return _.toNumber(allocation.weight || 0);
        });

        return _.map(allocations, (allocation) => {
            const { merchantId, weight } = allocation;

            const { name } = _.find(this.props.merchants, { merchantId }) || {};

            const normalizedWeight = summedWeight ? (weight / summedWeight) * 100 : 0;

            return {
                name,
                merchantId,
                weight: _.round(normalizedWeight)
            };
        });
    };

    getUserCurrencies = () => {
        const currenciesWithRules = _.reduce(this.props.rules, (acc, rule) => {
            if (rule.ruleType === ruleTypes.TARGET_ALLOCATION) {
                acc.push(rule.currency);
            }

            return acc;
        }, []);

        const currencies = _.uniq(_.reduce(this.props.paymentMerchants, (acc, merchant) => {
            acc.push(..._.map(merchant.paymentOptions, (paymentOption) => paymentOption.currency));

            return acc;
        }, []));

        return _.without(currencies, ...currenciesWithRules);
    };

    getTotalAllocationPercentage = () => {
        return _.sumBy(this.state.targetAllocations, (targetAllocation) => {
            return _.parseInt(targetAllocation.weight || 0);
        });
    };

    handleOnCurrencyChange = (currency) => {
        this.setState({ currency: currency, targetAllocations: this.buildTargetAllocations(currency) });
    };

    handleDeleteRule = () => {
        this.popoverDeleteRuleRef.current.openPopover(this.deleteButtonRef.current);
    };

    handleOnRuleTypeChange = (ruleType) => {
        if (ruleType !== this.state.ruleType) {
            this.clearState();
            if (ruleType === ruleTypes.ITEM_RULE) {
                const builtItemRule = [{
                    attribute: undefined,
                    value: undefined,
                    inputType: undefined,
                    key: crypto.randomUUID()
                }];
                this.setState({ builtItemRule });
            }
            this.setState({ ruleType });
        }
    };

    handleOnSubmit = () => {
        const { ruleType, merchantId, targetAllocations, currency } = this.state;
        const updatedRule = { ruleType };

        if (updatedRule.ruleType === ruleTypes.TARGET_ALLOCATION) {
            updatedRule.currency = currency;
            updatedRule.weights = _.map(targetAllocations, (targetAllocation) => {
                return {
                    merchantId: targetAllocation.merchantId,
                    weight: targetAllocation.weight || 0
                };
            });
        } else if (updatedRule.ruleType === ruleTypes.ITEM_RULE) {
            updatedRule.merchantId = merchantId;
            updatedRule.itemRule = _.reduce(this.state.builtItemRule, (acc, v) => {
                _.set(acc, `${v.attribute}`, this.convertStringToRegex(v.value, v.inputType));

                return acc;
            }, {});
        }

        this.props.onSubmit(updatedRule);
    };

    handleOnTargetWeightChange = (merchantId, event) => {
        const { targetAllocations } = this.state;

        const updatedTargetAllocations = _.clone(targetAllocations);

        const indexOfMerchant = _.findIndex(targetAllocations, { merchantId });

        const formattedInput = _.replace(event.target.value, /\./g, '');

        const newWeight = _.floor(_.parseInt(formattedInput || 0));

        _.set(updatedTargetAllocations, [indexOfMerchant, 'weight'], newWeight);

        this.setState({ targetAllocations: updatedTargetAllocations });
    };

    renderRuleType = () => {
        const { ruleType } = this.state;

        return (
            <div className="rule-type">
                <div className="title">Rule Type</div>
                <div className={classNames('rule-type-buttons', { disabled: this.props.rule })}>
                    <div

                        className={classNames('rule-type-button item-rule', { selected: ruleType === ruleTypes.ITEM_RULE })}
                        onClick={() => !this.props.rule && this.handleOnRuleTypeChange(ruleTypes.ITEM_RULE)}
                    >
                        Item Routing
                    </div>
                    <div
                        className={classNames('rule-type-button target-allocation', { selected: ruleType === ruleTypes.TARGET_ALLOCATION })}
                        onClick={() => !this.props.rule && this.handleOnRuleTypeChange(ruleTypes.TARGET_ALLOCATION)}
                    >
                        Target Allocation
                    </div>
                </div>
            </div>
        );
    };

    calculateTotalProccessingVolumeByCurrency = (currency) => {
        return _.reduce(_.get(this.props, 'paymentMerchants'), (totalProcessingVolumeByCurrency, merchant) => {
            return merchant.monthlyProcessingVolumesByCurrency[currency] ?
                totalProcessingVolumeByCurrency + merchant.monthlyProcessingVolumesByCurrency[currency] :
                totalProcessingVolumeByCurrency;
        }, 0);
    };

    calculateSingleMerchantPercentageByCurrency = (merchantId) => {
        const monthlyProcessingVolumesByCurrency = _.get(_.find(_.get(this.props, 'paymentMerchants'),
            (merchant) => merchant.merchantId === merchantId), 'monthlyProcessingVolumesByCurrency');

        if (_.isUndefined(monthlyProcessingVolumesByCurrency)) {
            return 0;
        }

        const totalProcessingVolume = this.calculateTotalProccessingVolumeByCurrency(this.state.currency);
        const singleMonthProcessingVolumeByCurrency = _.get(monthlyProcessingVolumesByCurrency, [this.state.currency]);

        if (_.isUndefined(singleMonthProcessingVolumeByCurrency) || !totalProcessingVolume) {
            return 0;
        }

        const calculatedProcessingVolumePercentage = singleMonthProcessingVolumeByCurrency / totalProcessingVolume * 100;

        return !_.round(calculatedProcessingVolumePercentage) && calculatedProcessingVolumePercentage ?
            '< 1' :
            _.round(calculatedProcessingVolumePercentage);
    };

    renderMerchants = () => {
        const totalPercentage = this.getTotalAllocationPercentage();

        return (
            <div className="merchant-weights">
                <div className="merchant header">
                    <div className="merchant-name">MERCHANT</div>
                    <div className="merchant-id">MERCHANT ID</div>
                    <div className="mtd-volume-percentage">CURRENT MTD<br />VOLUME %</div>
                    <div className="target-allocation-percentage">TARGET<br/>ALLOCATIONS</div>
                </div>
                {_.map(this.state.targetAllocations, (merchant) => this.renderMerchant(merchant))}
                <div className="footer">
                    <div className="total-percentage">{totalPercentage}%</div>
                    <div className={classNames('remaining-percentage', {
                        invalid: totalPercentage !== 100
                    })}>Must total 100%
                    </div>
                </div>
            </div>
        );
    };

    renderMerchant = (merchant) => {
        const { merchantId, name, weight = 0 } = merchant;

        return <div className="merchant" key={merchantId}>
            <div className="merchant-name-and-id">
                <div className="merchant-name">{name}</div>
                <div className="merchant-id">{merchantId}</div>
            </div>
            <div className="merchant-id">{merchantId}</div>
            <div className="mtd-volume-percentage">
                {`${this.calculateSingleMerchantPercentageByCurrency(merchantId)} %`}
            </div>
            <div className="input target-allocation-percentage">
                <EmvioValidatedInput
                    name="targetAllocationInput"
                    onChange={(event) => this.handleOnTargetWeightChange(merchantId, event)}
                    type="text"
                    validations={[]}
                    value={weight}
                    shouldEnforceNumber
                />
                <div className="percentage-display">%</div>
            </div>
        </div>;
    };

    renderTargetAllocationRule = () => {
        const options = this.getUserCurrencies().map((currency) => {
            return {
                value: currency,
                label: currency
            };
        });

        return (
            <div className="rule target-allocation-rule">
                <div className="title">Rule Info</div>
                {
                    !_.isEmpty(this.props.rule)
                        ? <EmvioValidatedInput
                            className="currency"
                            labelText="CURRENCY"
                            name="currency"
                            validations={['required']}
                            value={this.state.currency}
                            disabled={true}
                        />
                        : <EmvioSelect
                            className="currency selector"
                            label="CURRENCY"
                            options={options}
                            onChange={this.handleOnCurrencyChange}
                            required={true}
                        />
                }
                {this.state.currency ? this.renderMerchants() : null}
            </div>
        );
    };

    handleMerchantSelection = (merchantId) => {
        this.setState({ merchantId });
    };

    getAttributeOptions = (index) => {
        const usedAttributes = _.without(_.map(this.state.builtItemRule, (thing) => thing.attribute), _.get(this.state, `builtItemRule[${index}].attribute`));

        const remainingAttributes = _.difference(allAttributes, usedAttributes);

        const options = _.map(remainingAttributes, (attribute) => {
            return {
                value: attribute,
                name: _.startCase(attribute)
            };
        });

        return options;
    };

    handleOnAttributeChange = (selectedValue, index) => {
        const updatedItemRule = _.cloneDeep(this.state.builtItemRule);

        updatedItemRule[index].attribute = selectedValue;

        this.setState({ builtItemRule: updatedItemRule });
    };

    handleInputTypeChange = (selectedValue, index) => {
        const updatedItemRule = _.cloneDeep(this.state.builtItemRule);

        updatedItemRule[index].inputType = selectedValue;

        this.setState({ builtItemRule: updatedItemRule });
    };

    handleItemRuleInput = (event, index) => {
        const updatedItemRule = _.cloneDeep(this.state.builtItemRule);

        updatedItemRule[index].value = event.target.value;

        this.setState({ builtItemRule: updatedItemRule });

    };

    handleAddItemRuleAttribute = () => {
        const updatedItemRule = _.cloneDeep(this.state.builtItemRule) || [];

        updatedItemRule.push({ attribute: undefined, value: undefined, inputType: undefined, key: crypto.randomUUID() });

        this.setState({ builtItemRule: updatedItemRule });
    };

    handleDeleteAttribute = (index) => {
        const updatedItemRule = _.cloneDeep(this.state.builtItemRule);

        updatedItemRule.splice(index, 1);

        this.setState({ builtItemRule: updatedItemRule });
    };

    renderItemRuleAttribute = (attribute, index) => {
        const inputOptions = [
            {
                value: inputTypes.REGEX,
                name: 'RegEx (ECMAScript)'
            },
            {
                value: inputTypes.CONTAINS,
                name: 'Contains'
            },
            {
                value: inputTypes.DOES_NOT_CONTAIN,
                name: 'Does Not Contain'
            },
            {
                value: inputTypes.MATCHES_EXACTLY,
                name: 'Matches Exactly'
            },
            {
                value: inputTypes.DOES_NOT_MATCH_EXACTLY,
                name: 'Does Not Match Exactly'
            }
        ];

        return (
            <div className="item-rule-attribute" key={attribute.key}>
                <div className="header">{index > 0 ? 'And If' : 'If'}</div>
                <EmvioNewSelect
                    className="attribute-selector"
                    label="ATTRIBUTE"
                    options={this.getAttributeOptions(index)}
                    onChange={(selectedValue) => this.handleOnAttributeChange(selectedValue, index)}
                    value={attribute.attribute || ''}
                />
                <EmvioNewSelect
                    className="filter-type-selector"
                    label="TYPE"
                    options={inputOptions}
                    onChange={(selectedValue) => this.handleInputTypeChange(selectedValue, index)}
                    value={attribute.inputType}
                />
                <EmvioValidatedInput
                    placeholder="Enter text"
                    className="filter-string"
                    name="filter-string"
                    validations={['required']}
                    type="text"
                    shouldNotTrim="true"
                    onChange={(event) => this.handleItemRuleInput(event, index)}
                    value={attribute.value || ''}
                />
                <div className="delete-attribute">
                    {_.size(this.state.builtItemRule) > 1 ?
                        <span className="icon-close" onClick={() => this.handleDeleteAttribute(index)}/> : null}
                </div>
            </div>
        );
    };

    renderItemRuleAttributes = () => {
        return (
            <div className="item-rule-attributes">
                <div className="item-rule-header">
                    Attributes
                </div>
                {_.map(this.state.builtItemRule, this.renderItemRuleAttribute)}
                <div className="footer">
                    {_.size(this.state.builtItemRule) < maxAttributeCount
                        ? <Fragment>
                            <EmvioButton
                                type="button"
                                className="add-attribute button"
                                onClick={this.handleAddItemRuleAttribute}
                            >
                                +
                            </EmvioButton>
                            <div
                                className="add-attribute text"
                                onClick={this.handleAddItemRuleAttribute}
                            >
                                Add Another Attribute
                            </div>
                        </Fragment>
                        : null
                    }
                </div>
            </div>
        );
    };

    renderItemRule = () => {
        const merchantOptions = _.map(this.props.paymentMerchants, (paymentMerchant) => {
            const { merchantId } = paymentMerchant;

            return {
                value: paymentMerchant.merchantId,
                label: `${_.get(_.find(this.props.merchants, { merchantId }), 'name')}, ${paymentMerchant.merchantId}`
            };
        });

        return (
            <div className="rule item-rule">
                <div className="title">Rule Info</div>
                {
                    !_.isEmpty(this.props.rule)
                        ? <EmvioValidatedInput
                            className="merchant"
                            labelText="MERCHANT"
                            name="merchant"
                            validations={['required']}
                            value={`${_.get(_.find(this.props.merchants, { merchantId: this.state.merchantId }), 'name')}, ${this.state.merchantId}`}
                            disabled={true}
                        />
                        : <EmvioSelect
                            className="merchant selector"
                            label="MERCHANT"
                            options={merchantOptions}
                            onChange={this.handleMerchantSelection}
                            required={true}
                        />
                }
                {this.state.merchantId ? this.renderItemRuleAttributes() : null}
            </div>
        );
    };

    render() {
        const modalTitle = _.isEmpty(this.props.rule) ? 'Add Routing Rule' : 'Edit Routing Rule';

        return (
            <Modal
                className="emvio-payment-rule-modal"
                uniqueModalName={this.props.uniqueModalName}
                title={modalTitle}
                onClose={this.handleOnClose}
            >
                <EmvioValidatedForm
                    className="payment-rule-form"
                    onSubmit={this.handleOnSubmit}
                    getButtonRef={() => this.submitButtonRef.current}
                    hasExternalErrors={this.hasExternalErrors()}
                >
                    {this.renderRuleType()}
                    {this.state.ruleType === ruleTypes.ITEM_RULE
                        ? this.renderItemRule()
                        : this.renderTargetAllocationRule()
                    }
                    <div className="payment-rule-buttons">
                        <EmvioValidatedButton isLoading={this.props.isEditRulesLoading} ref={this.submitButtonRef}>
                            Save
                        </EmvioValidatedButton>
                        {this.props.rule
                            ? <Fragment>
                                <EmvioButton
                                    className="delete"
                                    ref={this.deleteButtonRef}
                                    invertColors
                                    isLoading={this.props.isEditRulesLoading}
                                    onClick={this.handleDeleteRule}
                                    size="large"
                                    variant="secondary"
                                    type="button"
                                >
                                    Delete
                                </EmvioButton>
                                <EmvioConfirmation
                                    buttonText="Yes"
                                    onConfirm={() => this.props.onSubmit(null)}
                                    text="Delete user rule?"
                                    ref={this.popoverDeleteRuleRef}
                                />
                            </Fragment>
                            : null
                        }
                    </div>
                </EmvioValidatedForm>
            </Modal>
        );
    }
}

export default UserRouteForm;
